import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

const ProfileReview = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [expanded, setExpanded] = useState(false); // For bio toggle

  useEffect(() => {
    let mounted = true;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setErrorMessage("");

        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (!session?.user?.id) {
          if (mounted) {
            setErrorMessage("Your session has expired. Please sign in again.");
          }
          return;
        }

        const { data, error } = await supabase
          .from("creator_profiles")
          .select("*")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (mounted) {
          setProfile(data || null);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);

        if (mounted) {
          setErrorMessage(
            error?.message || "We could not load your profile."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-full bg-transparent text-slate-100">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center">
          <p className="text-sm text-slate-300">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-full bg-transparent text-slate-100">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center">
          <h2 className="text-xl font-semibold text-cyan-300">
            Unable to load your profile
          </h2>

          <p className="mt-3 text-sm text-slate-300">
            {errorMessage}
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-5 py-2 text-sm font-semibold text-white hover:brightness-110"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-full bg-transparent text-slate-100">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center">
          <h2 className="text-xl font-semibold text-cyan-300">
            Complete Your Profile
          </h2>

          <p className="mt-3 text-sm text-slate-300">
            Your creator profile has not been created yet.
          </p>

          <button
            type="button"
            onClick={() => navigate("/profile/edit")}
            className="mt-5 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-5 py-2 text-sm font-semibold text-white hover:brightness-110"
          >
            Create Profile
          </button>
        </div>
      </div>
    );
  }

  const tools =
    profile.tools?.split(",").map((t) => t.trim()).filter(Boolean) || [];
  const portfolioLinks =
    profile.portfolio_links
      ?.split(",")
      .map((link) => link.trim())
      .filter(Boolean) || [];

  const bioTooLong = (profile.bio || "").length > 300;
  const displayBio =
    bioTooLong && !expanded
      ? `${profile.bio.substring(0, 300)}...`
      : profile.bio;

  return (
    <div className="min-h-full bg-transparent text-slate-100">
      <div className="mx-auto max-w-5xl px-4 py-6 md:py-8">
        {/* Heading */}
        <h2 className="text-xl font-semibold text-cyan-300 md:text-2xl">
          👤 Your Profile
        </h2>

        {/* Info banner */}
        <div className="mt-2 rounded-2xl border border-slate-700/60 bg-slate-900/80 px-4 py-3 text-xs text-indigo-100 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
          🧩{" "}
          <span className="font-semibold">
            On Dreamframe, every user can both create and collaborate.
          </span>{" "}
          Start projects or join others — your single profile powers both.
        </div>

        {/* Summary row */}
        <div className="mt-5 grid gap-4 md:gap-5 md:grid-cols-3">
          {/* Avatar + basics */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 text-center shadow-lg shadow-fuchsia-500/20 backdrop-blur">
            <img
              src={
                profile.profile_image_url?.trim()
                  ? profile.profile_image_url
                  : "/user.png"
              }
              alt="avatar"
              className="mx-auto h-24 w-24 rounded-full border-2 border-cyan-400/70 object-cover shadow-md shadow-cyan-500/40"
            />
            <h3 className="mt-3 text-base font-semibold text-slate-100">
              {profile.name}
            </h3>
            <p className="text-xs text-slate-400">{profile.role}</p>
            {profile.tagline && (
              <p className="mt-3 text-xs text-slate-200">
                <span className="font-semibold text-slate-100">Tagline: </span>
                {profile.tagline}
              </p>
            )}
          </div>

          {/* Genres, tools, bio */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
            <h4 className="text-sm font-semibold text-sky-200">
              Preferred Genres
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.genres || "No genres added yet."}
            </p>

            <h4 className="mt-4 text-sm font-semibold text-sky-200">
              Top Tools Used
            </h4>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tools.length ? (
                tools.map((tool, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-cyan-500/10 px-2.5 py-0.5 text-[11px] text-cyan-200"
                  >
                    {tool}
                  </span>
                ))
              ) : (
                <p className="text-xs text-slate-400">No tools listed yet.</p>
              )}
            </div>

            <h4 className="mt-4 text-sm font-semibold text-sky-200">Bio</h4>
            <div className="mt-1 text-xs leading-relaxed text-slate-200 whitespace-pre-wrap">
              {displayBio || "No bio added yet."}
              {bioTooLong && (
                <span
                  onClick={() => setExpanded(!expanded)}
                  className="mt-2 block cursor-pointer text-[11px] font-semibold text-cyan-300 hover:text-cyan-200"
                >
                  {expanded ? "Show Less ▲" : "Read More ▼"}
                </span>
              )}
            </div>
          </div>

          {/* Availability, timezone, work style */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
            <h4 className="text-sm font-semibold text-emerald-200">
              General Availability
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.availability || "Not specified"}
            </p>

            <h4 className="mt-4 text-sm font-semibold text-emerald-200">
              Time Zone
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.time_zone || "Not specified"}
            </p>

            <h4 className="mt-4 text-sm font-semibold text-emerald-200">
              Preferred Work Style
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.collab_style || "Not specified"}
            </p>
          </div>
        </div>

        {/* Portfolio */}
        <div className="mt-6 rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
          <h4 className="text-sm font-semibold text-sky-200">Portfolio Links</h4>
          {portfolioLinks.length ? (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-200">
              {portfolioLinks.map((link, i) => (
                <li key={i}>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-300 underline decoration-cyan-400/70 underline-offset-2 hover:text-cyan-200"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-slate-400">
              No portfolio links added yet.
            </p>
          )}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate("/profile/edit")}
            className="inline-flex items-center rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-fuchsia-500/40 hover:brightness-110"
          >
            ✏️ Edit Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileReview;
